<template>
	<app-page v-loading="dashboardsLoading || dashboardLoading" :class="themeCssClass">

		<empty-state
			v-id="'empty-state-dashboard'"
			v-if="!dashboardsLoading && !activeDashboard"
			icon="dashboard"
			title="Your data, your way"
			:description="isGroupOrDashboardAdmin ? 'Track various statistics in a customizable, visual dashboard' : 'Contact your group admin to get access to dashboards'"
		>
            
			<btn
                v-id="'add-dashboard'"
                v-if="isGroupOrDashboardAdmin && !mobile"
				primary 
				raised 
				@click="createDashboard"
			>
                Create a dashboard
            </btn>

            <create-dashboard-modal
                v-if="group"
                ref="createDashboardModal"
                :group="group"
                @create="onDashboardCreate"
            />
		</empty-state>

		<section v-if="group && activeDashboard" :class="['dashboard-body',{ 'dashboard-mobile': showMobileDashboardPicker }]">

			<toolbar v-id="'toolbar'" :theme="theme" class="toolbar-dashboard pt-1 px-1 pb-1g5">
				<toolbar-group v-if="!mobile && activeDashboard && activeDashboard.permissions.hasAny(['WIDGET_ADD'])">
					<toolbar-button 
						v-id="'add-widget'"
                        v-if="activeDashboard.permissions.WIDGET_ADD"
						icon="add" 
						title="Add widget" 
						@click="addWidget" 
					/>
				</toolbar-group>

				<toolbar-group v-if="!showMobileDashboardPicker && activeDashboard">
                    <toolbar-item>
						<dashboard-picker
                            :value="activeDashboard"
                            :dashboards="dashboards"
                            :allow-create="isGroupOrDashboardAdmin && !mobile"
                            @add="createDashboard"
                            @change="setActiveDashboard"
                        />
					</toolbar-item>
				</toolbar-group>

				<toolbar-group v-if="activeDashboard && hasWidgets && activeDashboard.permissions.hasAny(['DASHBOARD_DATE_RANGE_EDIT', 'DASHBOARD_DATE_RANGE_GROUPING_EDIT'])">
				
					<toolbar-item>
						<date-range-picker
                            class="date-range-picker"
                            :disabled="!activeDashboard.permissions.DASHBOARD_DATE_RANGE_EDIT"
                            :value="activeDashboard.dateRange"
							:start="startDateFilter"
							:end="endDateFilter"
                            :min="minDateInDateRange"
							:max="maxDateInDateRange"
                            :range-offset="activeDashboard.rangeOffset"
                            :ranges="ranges"
							@change="onFilterDateRangeChange"
						>
							<btn icon="date_range" title="Date range" slot-scope="slot">
								{{ slot.rangeDisplay }}
							</btn>
						</date-range-picker>
					</toolbar-item>

                    <toolbar-item>
						<date-range-grouping-picker
                            :disabled="!activeDashboard.permissions.DASHBOARD_DATE_RANGE_GROUPING_EDIT"
                            :value="groupByFilter"
                            :start="startDateFilter"
                            :end="endDateFilter"
                            @change="onFilterGroupByChange"
                        />
					</toolbar-item>
			
				</toolbar-group>

                <toolbar-group>
                    <toolbar-button
						icon="refresh" 
						title="Refresh" 
						@click="refresh" 
					/>
                </toolbar-group>

                <toolbar-group>
                    <toolbar-button
                        icon="cloud_download"
                        title="Export Dashboard"
                        @click="openExportOptions"
                    />
                </toolbar-group>

                <toolbar-group>
                    <toolbar-item>
						<widget-theme-picker
                            :value="groupByFilter"
                            :dashboard="activeDashboard"
                            class="widget-theme-picker"
                        />
					</toolbar-item>
                </toolbar-group>

                <toolbar-group v-if="activeDashboard && !mobile">
					<toolbar-button
                        v-if="isFullscreenEnabled && activeDashboard.permissions.TV_MODE_ACTIVATE"
						:icon="isFullscreenActive ? 'tv_off' : 'tv'" 
						:title="isFullscreenActive ? 'Exit TV mode' : 'TV mode'" 
						@click="startTV" 
					/>

                    <toolbar-item>
						<dropdown>
							<btn slot="toggle" icon="palette" title="Theme" />
							<list>
                                <list-header>Dashboard theme</list-header>
                                <list-item
                                    v-for="t in themes"
                                    :key="t.id"
									:primary-icon="theme == t.id ? 'check' : null" 
									:title="t.name" 
									clickable
                                    inset
									@click="setTheme(t.id)" 
								/>
							</list>
						</dropdown>
					</toolbar-item>
				</toolbar-group>

				<toolbar-group v-if="!mobile && activeDashboard && (isGroupOrDashboardAdmin || activeDashboard.permissions.hasAny(['DASHBOARD_EDIT', 'WIDGET_ADD']))">
					<toolbar-item>
						<dropdown v-id="'dashboard-settings'">
							<btn slot="toggle" icon="settings" title="Settings" />
							<list>
                                <list-item
                                    v-if="activeDashboard.permissions.DASHBOARD_EDIT"
									clickable
									primary-icon="settings" 
									title="Dashboard settings" 
									@click="editDashboard" 
								/>
                                <list-item
                                    v-if="isGroupOrDashboardAdmin"
									clickable
									primary-icon="supervisor_account" 
									title="Manage users" 
									@click="editUsers" 
								/>
                                <list-item
                                    v-if="isGroupOrDashboardAdmin"
									clickable
									primary-icon="group_work" 
									title="Manage group" 
									@click="editGroup" 
								/>
								<list-item
                                    v-if="activeDashboard.permissions.WIDGET_ADD"
									clickable
									primary-icon="add" 
									title="Add a widget…" 
									@click="addWidget" 
								/>
								<list-divider inset />
								<list-item
                                    v-if="isGroupOrDashboardAdmin"
									clickable
									primary-icon="dashboard" 
									title="New dashboard…" 
									@click="createDashboard" 
								/>
								<list-item
                                    v-if="isGroupOrDashboardAdmin"
									clickable
									primary-icon="content_copy" 
									title="Make a copy…" 
									@click="copyDashboard" 
								/>
                                <template v-if="isGroupOrDashboardAdmin">
								    <list-divider inset />
								    <list-item
									    clickable
									    primary-icon="delete" 
									    title="Delete" 
									    @click="deleteDashboard" 
								    />
                                </template>
							</list>
						</dropdown>
					</toolbar-item>
				</toolbar-group>
			</toolbar>

			<section id="dashboard-body" :class="['px-1','pb-1','pt-1',themeCssClass, { 'dashboard-body': !mobile }]">
				<dashboard
                    ref="dashboard"
                    :group="group"
                    :id="activeDashboard.dashboardID"
                    @add="addWidget"
                    @loading="dashboardLoading = true"
                    @loaded="dashboardLoading = false"
                /> 
			</section>


            <add-widget-modal
                ref="addWidgetModal"
                v-show="activeDashboard.permissions.WIDGET_ADD"
                :dashboard="activeDashboard"
                @add="onWidgetAdd"
            />

            <create-dashboard-modal
                v-show="group"
                ref="createDashboardModal"
                :group="group"
                @create="onDashboardCreate"
            />

            <template v-if="isGroupOrDashboardAdmin">
                <copy-dashboard-modal
                    ref="copyDashboardModal"
                    :group="group"
                    :dashboard="activeDashboard"
                    @create="onDashboardCreate"
                />		        
		        <delete-dashboard-modal
                    ref="deleteDashboardModal"
                    :group="group"
                    :dashboard="activeDashboard"
                />
                <edit-users-modal
                    ref="editUsersModal"
                    :group="group"
                    :dashboard="activeDashboard"
                    @save="onDashboardSave"
                />
            </template>

		    <edit-dashboard-modal
                ref="editDashboardModal"
                :group="group"
                :dashboard="activeDashboard"
                @save="onDashboardSave"
            />

            <template v-if="showMobileDashboardPicker">
                <div class="mobile-dashboard-picker-toolbar">
                    <toolbar v-id="'mobile-toolbar'" class="toolbar-dashboard-mobile">
				        <toolbar-group v-if="activeDashboard">
                            <toolbar-item>
						        <dashboard-picker
                                    :value="activeDashboard"
                                    :dashboards="dashboards"
                                    :allow-create="isGroupOrDashboardAdmin && !mobile"
                                    @add="createDashboard"
                                    @change="setActiveDashboard"
                                />
                            </toolbar-item>
				        </toolbar-group>
                    </toolbar>
                </div>
            </template>

		</section>

        <start-tv-modal
            ref="startTVModal"
            v-if="!mobile"
            @start="onTVStart"
        />

        <export-options-modal
            ref="exportOptionsModal"
            @onExport="onExport"
        />

	</app-page>
</template>


<script>
    import _ from 'lodash';
    import $ from 'jquery';
    import '@progress/kendo-ui';
    import moment from 'moment';
    import JSZip from 'jszip';
    import BigScreen from 'bigscreen';
    import { AsyncMixin } from 'pdk/mixins';
    import { CustomWidgetDisplayMixin } from 'dashboard-pdk/components/widgets/mixins';
    import { SimpleDataSourceProvider } from 'dashboard-pdk/data/providers/simple-data-source-provider';
    import { browser, notifications } from 'pdk/services';
    import AppConfig from '@/config';
    import { actions, getters } from 'dashboard-pdk/store';
    import { actions as appStoreActions, store, getters as appStoreGetters } from '@/store';
    import { account, auth } from '@/services';
    import themes from 'dashboard-pdk/themes';
    import { dashboards, widgets } from 'dashboard-pdk/services';
    import { getDateRanges } from 'pdk/src/components/date-range-picker';
    import {
        Dashboard,
        DashboardPicker,
        DateRangeGroupingPicker,
        LocationPicker,
        WidgetThemePicker
    } from 'dashboard-pdk/components/dashboards';
    import {
        AddWidgetModal,
        CopyDashboardModal,
		CreateDashboardModal,
		DeleteDashboardModal,
        EditDashboardModal,
        EditUsersModal
    } from 'dashboard-pdk/components/dashboards/modals';
    import { ExportOptionsModal, StartTvModal } from './modals';
    import { base64StringToBlob } from 'blob-util';


	export default {
		name: 'dashboard-page',
		components: {
			AddWidgetModal,
			CopyDashboardModal,
			CreateDashboardModal,
            Dashboard,
            DashboardPicker,
            DateRangeGroupingPicker,
			DeleteDashboardModal,
            EditDashboardModal,
            StartTvModal,
            EditUsersModal,
            ExportOptionsModal,
            WidgetThemePicker
        },
        mixins: [AsyncMixin, CustomWidgetDisplayMixin],
        provide() {
            const result = {};
            
            Object.defineProperty(result, 'theme', {
                enumerable: true,
                get: () => this.selectedTheme,
                set: (val) => { this.selectedTheme = val; }
            });

            return {
                dashboardConfig: result
            };
        },
        beforeRouteEnter(to, __, next) {
            store.dispatch(actions.INITIALIZE).then(() => {
                const groups = store.state.dashboard.locationGroups;
                if (!groups.length) {
                    next({ name: 'no-groups-found', replace: true });
                    return;
                }

                if (!to.params.locationGroupId) {
                    const activeLocationGroupID = store.getters[getters.ACTIVE_LOCATION_GROUP_ID]();

                    if (activeLocationGroupID) {
                        next({ name: 'group-dashboards', params: { locationGroupId: activeLocationGroupID } });
                    }
                    else if (groups.length) {
                        next({ name: 'group-dashboards', params: { locationGroupId: groups[0].locationGroupID } });
                    }
                    else {
                        next({ name: 'no-groups-found' });
                    }
                }
                else {
                    store.dispatch(actions.LOCATION_GROUP_ACTIVATE, { locationGroupID: to.params.locationGroupId, dashboardID: to.query.dashboardId })
                        .then(() => {
                            account.getThemePreference().then((preferences) => {
                                next((vm) => {
                                    vm.theme = preferences.theme || 'light';
                                });
                            });
                        })
                        .catch((e) => {
                            if (e && e.response && e.response.status === 403) { //If the user does not have access to this dashboard, take them to the first dashboard in the group.
                                const dashboardID = store.getters[getters.ACTIVE_DASHBOARD_ID]();
                                store.dispatch(actions.DASHBOARD_ACTIVATE, {
                                    dashboardID: dashboardID
                                }).then(() => {
                                    account.getThemePreference().then((preferences) => {
                                        next((vm) => {
                                            vm.theme = preferences.theme || 'light';
                                        });
                                    });
                                }).catch((e) => {
                                    if (e.response.status === 403) { //If the user does not have access to any dashboards in the group, log them out.
                                        this.$store.dispatch(actions.CLEAR);
                                        this.$store.dispatch(appStoreActions.LOGOUT);
                                    }
                                });

                                notifications.open({ content: "User not authorized. Redirecting to default dashboard for group." });
                            }
                            else
                                next({ name: '404', replace: true });
                        });
                }
            });
        },
        beforeRouteUpdate(to, __, next) {
            store.dispatch(actions.INITIALIZE).then(() => {
                const groups = store.state.dashboard.locationGroups;
                if (!groups.length) {
                    next({ name: 'no-groups-found', replace: true });
                    return;
                }

                if (!to.params.locationGroupId) {
                    const activeLocationGroup = store.getters[getters.ACTIVE_LOCATION_GROUP];

                    if (activeLocationGroup) {
                        next({ name: 'group-dashboards', params: { locationGroupId: activeLocationGroup.locationGroupID } });
                    }
                    else if (groups.length) {
                        next({ name: 'group-dashboards', params: { locationGroupId: groups[0].locationGroupID } });
                    }
                    else {
                        next({ name: 'no-groups-found' });
                    }
                }
                else {
                    store.dispatch(actions.LOCATION_GROUP_ACTIVATE, { locationGroupID: to.params.locationGroupId })
                        .then(() => {
                            account.getThemePreference().then((preferences) => {
                                this.theme = preferences.theme || 'light';
                                next();
                            })
                        .catch(() => {
                            next({ name: '404', replace: true });
                        });
                    });                    
                }
            });
        },
        props: {
            locationGroupId: {
                type: String,
                required: true
            }
        },
		data() {
            return {
                groups: [],
                dateRangeGroupByTypes: [],
                minDateInDateRange: moment('1/1/1900').toDate(),
				maxDateInDateRange: moment().add(10, 'y').toDate(),
                relativeDateRangeUpdaterHandler: null,
                dashboardPickerFilter: null,
                isFullscreenActive: false,
                theme: null,
                themes,
                browser: browser.getBrowser(),
                keepAliveHandler: undefined,
                dashboardLoading: false,
                viewportSize: browser.getViewportSize(),
                ranges: getDateRanges()
			};
		},
        computed: {
            themeCssClass() {
                return `dashboard-theme-${this.theme}`;
            },
            selectedTheme() {
                return _.find(this.themes, (t) => t.id === this.theme);
            },
            mobile() {
				return this.browser.mobile;
            },
            showMobileDashboardPicker() {
				return ['xs', 'sm'].includes(this.viewportSize);
			},
            group() {
                if (!this.groups || !this.groups.length) return null;
                return _.find(this.groups, (g) => g.locationGroupID == this.locationGroupId);
            },
			dashboards() {
				return this.$store.state.dashboard.dashboards;
            },
            dashboardsLoading() {
                return typeof this.$store.state.dashboard.dashboards === 'undefined';
            },
			activeDashboard() {
				return this.$store.getters[getters.ACTIVE_DASHBOARD];
            },
            widgets() {
				return this.$store.getters[getters.WIDGETS]();
			},
			hasWidgets() {
				if(!this.activeDashboard || !this.widgets) return false;

				return this.widgets.length > 0;
            },
            groupIDFilter() {
                return this.locationGroupId;
            },
            startDateFilter() {
                if (!this.activeDashboard) return undefined;

                return this.activeDashboard.startDate;
			},
			endDateFilter() {
				if (!this.activeDashboard) return undefined;

                return this.activeDashboard.endDate;
			},
			groupByFilter() {
				if (!this.activeDashboard) return undefined;

                return this.activeDashboard.groupBy;
			},
            dateRangeDisplay() {
                if (!this.activeDashboard) return undefined;

				const format = 'M/D/YYYY';
				const start = moment(this.startDateFilter);
                const end = moment(this.endDateFilter);
                const isRelative = this.activeDashboard.dateRange == 9; // Custom range…

				if (start.isSame(end, 'day') && start.isSame(end, 'month') && start.isSame(end, 'year'))
					return start.format(format);
				

				if(isRelative) {
					const startOfMonth = start.clone().startOf('month');
					const endOfMonth = end.clone().endOf('month');

					if(start.isSame(end, 'year') && start.isSame(end, 'month') && start.isSame(startOfMonth, 'day') && end.isSame(endOfMonth, 'day'))
						return start.format('MMMM YYYY');
				}

				return `${start.format(format)} - ${end.format(format)}`;
            },
            isRelativeDateRange() {
                return this.activeDashboard.dateRange != 9; // Custom range…
            },
            isFullscreenEnabled() {
                return BigScreen.enabled;
            },
            isGroupOrDashboardAdmin() {
                return this.$store.getters[appStoreGetters.USER_GROUP_IS_ADMIN](this.locationGroupId) ||
                       this.$store.getters[appStoreGetters.USER_IS_DASHBOARD_ADMIN](store.getters[getters.ACTIVE_DASHBOARD_ID]());
            },
            appUpdateRequired() {
                return this.$store.getters[appStoreGetters.APP_UPDATE_REQUIRED];
            }
        },
        watch: {
            appUpdateRequired(value) {
                if (value) {
                    this.$store.dispatch(actions.WIDGETS_CLEAR);
                }
            }
        },
        created() {
            this.getGroups();
            this.getDateRangeGroupByTypes();

            BigScreen.onenter = () => {
                document.getElementsByTagName('body')[0].classList.add('fullscreen');
                this.isFullscreenActive = true;
            };
            BigScreen.onexit = () => {
                document.getElementsByTagName('body')[0].classList.remove('fullscreen');
                this.isFullscreenActive = false;
            };

            //token lifetime minus refreshThreshold converted to milliseconds 
            const refreshInterval = (this.$auth.tokenParsed.exp - this.$auth.tokenParsed.iat - AppConfig.auth.refreshThreshold)*1000;
            if (this.keepAliveHandler) {
                clearInterval(this.keepAliveHandler);
                this.keepAliveHandler = undefined;
            }
            this.keepAliveHandler = setInterval(() => {
                this.$auth.updateToken(-1)
                    .then((refreshed) => {
                        if (!refreshed)
                            this.$auth.clearToken();
                    }).catch(() => {
                        this.$auth.clearToken();
                    });
            }, refreshInterval);
        },
		mounted() {
			/*
			 * Start the relative date range updater when the next minute starts.
			 * This will ensure that the clock on the local machine and their dashboard are in sync
			 * when checking for relative date ranges by the minute.
			 */
			const now = moment();
			const start = moment().endOf('minute');
			const ms = start.diff(now, 'milliseconds');
			setTimeout(() => {
				this.beginRelativeDateRangeUpdater();
            }, ms);
        },
        beforeDestroy() {
            if (this.keepAliveHandler) {
                clearInterval(this.keepAliveHandler);
                this.keepAliveHandler = undefined;
            }
        },
        methods: {
            // Getting Data
            getGroups() {
                return this.async(account.getUserGroups())
                    .then((groupList) => {
                        this.groups = groupList;
                    });
            },
            getDateRangeGroupByTypes() {
                return this.async(dashboards.getDateRangeGroupByTypes())
                    .then((types) => {
                        this.dateRangeGroupByTypes = types;
                    });
            },

            //Export Data Section
            openExportOptions(e) {
                e.preventDefault();
                this.$refs.exportOptionsModal.open();
            },
            // Returns an array of promises to fetch the data for the widgets in the active dashboard.
            getWidgetExportPromises() {
                var promises = [];

                this.widgets.forEach((widget) => {
                    var widgetCopy = _.cloneDeep(widget);

                    widgetCopy.context = {
                        dashboard: this.activeDashboard,
                        widget: widgetCopy,
                        widgetType: widgets.getWidgetType(widgetCopy.widgetTypeID),
                        locationsEnabled: AppConfig.dashboards.locationsEnabled
                    };

                    const filename = `${widgetCopy.title} Export`;
                    const provider = this.getDataSourceProvider(widgetCopy);

                    if (!this.exportDisabled(provider)){
                        promises.push(new Promise((resolve, reject) => {
                            this.async(provider.getExportData())
                            .then((csv) => {
                                var file = { 
                                    fileName: filename,
                                    data: csv
                                };

                                resolve(file);
                            })
                            .catch((response) => {
                                reject(response);
                            });
                        }));
                    }
                });

                return promises;
            },
            // Event to capture the results of the export options modal.
            onExport(options){
                if (options.exportType === 'Widgets')
                    this.exportWidgets(options.fileType);
                else
                    this.exportDashboard(options.fileType);
            },
            // Export all the widgets in the dashboard into a single csv file and trigger a download.
            exportDashboard(fileType) {
                if (!this.hasWidgets) return;

                if (fileType === 'CSV' || fileType === 'XLSX'){
                    const promises = this.getWidgetExportPromises();
                    const ctx = this;

                    Promise.all(promises)
                    .then((values) => {
                        let content = '';
                        
                        values.forEach((file) => {
                            content = `${content}${file.fileName}\n${file.data}\n`;
                        });

                        if (fileType === 'XLSX')
                        {
                            this.async(dashboards.convertToSpreadsheet({ csvdata: content })
                            .then((response) => {
                                content = response.FileContents;

                                if (!window.navigator.msSaveBlob)
                                    content = `data:${response.ContentType};base64,${content}`;
                                else
                                    content = base64StringToBlob(content, response.ContentType);

                                ctx.download(content, `${ctx.activeDashboard.dashboardName}.xlsx`, response.ContentType);

                                notifications.open({
                                    content: 'Export complete'
                                });
                            })
                            .catch(() => {
                                notifications.open({
                                    content: 'Export failed'
                                });
                            }));
                        }
                        else {
                            content = `data:text/csv;charset=utf-8,${content}`;

                            ctx.download(content, `${ctx.activeDashboard.dashboardName}.csv`, 'text/csv');

                            notifications.open({
                                content: 'Export complete'
                            });
                        }
                    });
                }
                else {
                    const ctx = this;
                    const draw = kendo.drawing;
                    const dashboard = $('#dashboard-body');

                    kendo.pdf.defineFont({
                        "Roboto Condensed": "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto_condensed/robotocondensed-regular-webfont.ttf",
                        "Roboto Condensed|Bold": "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto_condensed/robotocondensed-bold-webfont.ttf",
                        "DejaVu Sans|Bold": "https://cdn.jsdelivr.net/npm/dejavu-sans-bold@1.0.0/fonts/dejavu-sans-bold-webfont.ttf"
                    });

                    if (!this.mobile) {
                        const widthWidget = _.maxBy(this.widgets, ((w) => {
                            return w.x + w.width;
                        }));

                        const width = widthWidget.x + widthWidget.width + 32;

                        const heightWidget = _.maxBy(this.widgets, ((w) => {
                            return w.y + w.height;
                        }));

                        const height = heightWidget.y + heightWidget.height + 32;

                        $(dashboard).css('width', `${width.toString()}px`);
                        $(dashboard).css('height', `${height.toString()}px`);
                    }

                    if (fileType === 'PNG')
                        draw.drawDOM("#dashboard-body")
                        .then((group) => {
                            return draw.exportImage(group);
                        })
                        .done((content) => {
                            if (window.navigator.msSaveBlob) //IE
                            {
                                content = content.replace(/^data:image\/[a-z]+;base64,/, "");
                                content = base64StringToBlob(content, 'image/png');
                            }

                            ctx.download(content, `${ctx.activeDashboard.dashboardName}.png`, 'image/png');

                            $(dashboard).css('width', '');
                            $(dashboard).css('height', '');

                            notifications.open({
                                content: 'Export complete'
                            });
                        });
                    else
                        draw.drawDOM("#dashboard-body")
                        .then((group) => {
                            group.options.set("pdf",{
                                title: ctx.activeDashboard.dashboardName,
                                creator: "RxLocal Pharmacy Portal"
                            });
                            if (window.navigator.msSaveBlob) //IE
                                draw.pdf.toBlob(group, ((blobData) => {
                                    ctx.download(blobData, `${ctx.activeDashboard.dashboardName}.pdf`, 'application/pdf');
                                }));
                            else
                                draw.pdf.toDataURL(group, ((data) => {
                                    ctx.download(data, `${ctx.activeDashboard.dashboardName}.pdf`, 'application/pdf');
                                }));

                            $(dashboard).css('width', '');
                            $(dashboard).css('height', '');

                            notifications.open({
                                content: 'Export complete'
                            });
                        });
                }
            },
            // Export individual widgets into csv files, compress them into a single zip file, and trigger a dowload.
            exportWidgets(fileType) {
                if (!this.hasWidgets) return;

                const zip = new JSZip();
                const promises = this.getWidgetExportPromises();

                if (fileType === 'CSV') {
                    Promise.all(promises)
                    .then((values) => {
                        values.forEach((file) => {
                            var blobData = new Blob([file.data], {type: 'text/csv'});
                            if (zip.file(`${file.fileName}.csv`)) {
                                for (var i = 1; 1 < 100; i++) {
                                    if (!zip.file(`${file.fileName}${i.toString()}`)) {
                                        file.fileName = `${file.fileName}${i.toString()}`;
                                        break;
                                    }
                                }
                            }
                            zip.file(`${file.fileName}.csv`, blobData);
                        });

                        this.compressAndDownload(zip);
                    });
                }
                else {
                    var xlPromises = [];

                    Promise.all(promises)
                    .then((values) => {
                        values.forEach((file) => {
                            var csvFile = { csvdata: file.data, fileName: file.fileName };
                            xlPromises.push(new Promise((resolve, reject) => {
                                this.async(dashboards.convertToSpreadsheet(csvFile))
                                .then((response) => {
                                    resolve(response);
                                })
                                .catch((response) => {
                                    reject(response);
                                });
                            }));
                        });

                        Promise.all(xlPromises)
                        .then((values) => {
                            values.forEach((file) => {
                                var blobData = base64StringToBlob(file.FileContents, file.ContentType);
                                if (zip.file(`${file.FileDownloadName}.xlsx`)) {
                                    for (var i = 1; 1 < 100; i++) {
                                        if (!zip.file(`${file.FileDownloadName}${i.toString()}.xlsx`)) {
                                            file.FileDownloadName = `${file.FileDownloadName}${i.toString()}`;
                                            break;
                                        }
                                    }
                                }
                                zip.file(`${file.FileDownloadName}.xlsx`, blobData);
                            });

                            this.compressAndDownload(zip);
                        });
                    });
                }
            },
            compressAndDownload(zip){
                if (window.navigator.msSaveBlob) { //IE
                    zip.generateAsync({type: "blob"})
                    .then((content) => {
                        this.download(content, 'export.zip', 'application/zip');

                        notifications.open({
                            content: 'Export complete'
                        });
                    })
                    .catch((response) => {
                        notifications.open({
                            content: 'Export failed'
                        });
                    });
                }
                else {
                    zip.generateAsync({type: "base64"})
                    .then((content) => {
                        this.download(`data:application/zip;base64,${content}`, 'export.zip', 'application/zip');

                        notifications.open({
                            content: 'Export complete'
                        });
                    })
                    .catch((response) => {
                        notifications.open({
                            content: 'Export failed'
                        });
                    });
                }
            },
            // Check whether a widget supports a data export.
            exportDisabled(widgetProvider) {
                if (!widgetProvider) return true;
                if ((!widgetProvider instanceof SimpleDataSourceProvider)) return true;               
                if ((!widgetProvider.widgetContext.config.dataUrl || !widgetProvider.widgetContext.config.dataUrl.length) &&
                    (!widgetProvider.widgetContext.config.googleSpreadsheetID))
                    return true;

                return false;
            },
            // Triggers the browser to download the content. fileType should be valid MIME type.
            download(content, filename, fileType) {
                if (window.navigator.msSaveBlob) { //IE
                    var blobData;

                    if (!(content instanceof Blob))
                        blobData = new Blob([content], {type: fileType});
                    else
                        blobData = content;

                    window.navigator.msSaveOrOpenBlob(blobData, filename);
                }
                else {
                    const link = document.createElement('a');
                    link.setAttribute('href', content);
                    link.setAttribute('download', filename);
                    link.setAttribute('target', '_blank');
                    
                    if (window.navigator.userAgent.search("Firefox") !== -1) { //Firefox
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    }
                    else {
                        link.click();
                    }
                }
            },

            // Widgets
            addWidget() {
				this.$refs.addWidgetModal.open();
            },
            onWidgetAdd() {
                
            },

            // Dashboard
            copyDashboard() {
				this.$refs.copyDashboardModal.open();
			},
			createDashboard() {
                this.$refs.createDashboardModal.open();
            },
			deleteDashboard() {
				this.$refs.deleteDashboardModal.open();
			},
			editDashboard() {
				this.$refs.editDashboardModal.open();
            },
			setActiveDashboard(dashboard) {
                this.$store.dispatch(actions.DASHBOARD_ACTIVATE, {
					dashboardID: dashboard.dashboardID
				});
            },
			onDashboardCreate() {
				notifications.open({
					content: 'Dashboard created'
				});
			},
			onDashboardSave() {
				notifications.open({
					content: 'Dashboard saved'
				});
            },
            refresh() {
                if (this.$refs.dashboard)
                    this.$refs.dashboard.renew();
            },

            // Groups
            editGroup() {
                this.$router.push({
                    name: 'manage-group',
                    params: {
                        locationGroupId: this.locationGroupId
                    }
                });
            },

            // Themes
            setTheme(themeID) {
                this.theme = themeID;

                store.dispatch(appStoreActions.THEME_SET, ({
                    theme: themeID
                })).then(() => {
                    // This refresh is needed due to a Kendo issue where some of the colors (category axis) aren't updated on a redraw and instead require the entire chart to be destroyed/rebuilt.
                    this.refresh();
                });
            },

            // Date Ranges
            beginRelativeDateRangeUpdater() {
                /*
			     * Keeps the date range picker up-to-date in case the selected range is a relative one
			     * and the dashboard is open when midnight hits.
			     */

				var hasDayChanged = false,
					lastDateChecked = moment();

				this.endRelativeDateRangeUpdater();

                this.relativeDateRangeUpdaterHandler = setInterval(() => {
                    if (!this.activeDashboard) return;

					var now = moment();
					hasDayChanged = !now.isSame(lastDateChecked, 'day');
					lastDateChecked = now.clone();

					if (hasDayChanged && this.isRelativeDateRange)
                    {
                        var filter = [
                            this.activeDashboard.dateRange,
                            moment(this.activeDashboard.startDate).add(1, 'days').format(),
                            moment(this.activeDashboard.endDate).add(1, 'days').format(),
                            this.activeDashboard.groupBy,
                            this.activeDashboard.rangeOffset
                        ];
                        
                        //Copy the array to itself to force the date updates
                        this.ranges = this.ranges.slice(0);
                        this.setFilterDateRange(...filter);
                    }					
				}, 60000);
			},
			endRelativeDateRangeUpdater() {
				if (this.relativeDateRangeUpdaterHandler)
					this.clearInterval(this.relativeDateRangeUpdaterHandler);				
			},
			getTotalDaysDifference(start, end) {
				const _start = moment(start);
				let _end = moment(end);
				if(_end.isAfter(moment()))
					_end = moment();
				
				const days = _end.diff(_start, 'days');

				return days;
            },
            setFilterDateRange(dateRange, startDate, endDate, groupBy, rangeOffset) {
                return this.async(this.$store.dispatch(actions.DASHBOARD_DATE_RANGE_SET, {
                    dashboardID: this.activeDashboard.dashboardID,
                    dateRange,
					startDate,
					endDate,
                    groupBy,
                    rangeOffset: parseInt(rangeOffset, 10)
				}));
            },
            onFilterGroupByChange(groupBy) {
                const d = this.activeDashboard;

                this.setFilterDateRange(
                    d.dateRange,
                    d.startDate,
                    d.endDate,
                    groupBy,
                    d.rangeOffset
                );
            },
            onFilterDateRangeChange(e) {
                const d = this.activeDashboard;
                
                this.setFilterDateRange(
                    e.value, 
                    e.start, 
                    e.end, 
                    d.groupBy,
                    e.rangeOffset
                );
            },

            // Fullscreen
            beginFullscreen() {
                BigScreen.toggle();
            },
            startTV() {
                this.$refs.startTVModal.open();
            },
            onTVStart() {
                
            },

            // Users
            editUsers() {
                this.$refs.editUsersModal.open();
            }
		}
	};
</script>


<style lang="scss">
@import '~@/assets/styles/references';

.mobile-dashboard-picker-toolbar { position: fixed; z-index: $zindex-fixed; top: $app-bar-height - 1px; left: 0; right: 0; @include z-depth-1; }
.mobile-dashboard-picker-toolbar-filler { height: $app-bar-height; }

.toolbar-dashboard,
.toolbar-dashboard-mobile{ flex-wrap: wrap; width: 100%; padding-bottom: gx(0.5) !important;
    .icon-dropdown { display: none; }
	> ul{ margin-bottom: gx(0.5); }
}

.dashboard-mobile {
    .toolbar-dashboard { margin-top: $app-bar-height; }
}

.dashboard-body { height: 100%; }

.toolbar-dashboard-mobile.toolbar-lg { width: auto; margin: 0;
    > ul { width: 100%; margin: 0; display: flex; border: 0;
        > li { flex: 1 1 auto; justify-content: flex-start; height: auto;
            .dropdown { width: 100%; }
            .dropdown-toggle {
                .btn { height: auto; justify-content: flex-start; width: 100%;
                    .btn-content { padding-top: gx(0.5); padding-bottom: gx(0.5); }
                    .btn-icon { margin-right: gx(1); font-size: 24px; }
                    &:after { right: 26px; }
                }
            }
        }
    }
}

body:fullscreen { background-color: $body-bg; }
html:fullscreen { background-color: $body-bg; }

.widget-theme-picker {
    .icon-dropdown {
        visibility: hidden;
    }
}

.fullscreen {
    .appbar,
    .drawer-wrapper { display: none; }
}

</style>
